import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, LogOut, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth, login } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

export const Navbar = ({ transparent = false, onPlan }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const goPlan = () => {
    if (onPlan) onPlan();
    else navigate("/#plan");
  };

  const section = (id) => {
    if (pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const mobileSection = (id) => {
    setOpen(false);
    section(id);
  };

  const publicLinks = [
    { label: "About Us", id: "about" },
    { label: "Buy Now", id: "marketplace", to: "/services" },
    { label: "Events", id: "events" },
    { label: "Our Work", id: "work" },
    { label: "Past Work", id: "past-work", to: "/past-work" },
  ];

  const teamLinks = [
    { to: "/dashboard", label: "Dashboard", id: "nav-dashboard-link" },
    { to: "/create", label: "Create Event", id: "nav-create-event-link" },
    { to: "/admin/services", label: "Services", id: "nav-services-link", perm: "marketplace" },
    { to: "/past-work/manage", label: "Past Work", id: "nav-pastwork-link", perm: "pastwork" },
    { to: "/admin", label: "Team", id: "nav-admin-link", perm: "team" },
  ].filter((l) => user?.status === "approved" && (!l.perm || can(user, l.perm)));

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-colors",
        transparent ? "border-b border-white/10 bg-black/20 backdrop-blur-xl" : "border-b border-border bg-background/85 backdrop-blur-xl"
      )}
      data-testid="navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" className="group flex items-center gap-2" data-testid="brand-logo">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary transition-transform group-hover:scale-105">
            <Sparkles className="h-4 w-4 text-gold" />
          </span>
          <span className={cn("font-serif text-xl font-semibold tracking-tight", transparent && "text-white")}>God<span className="text-gold">inga</span></span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {!user &&
            publicLinks.map((l) =>
              l.to ? (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-${l.id}`}
                  className={cn(
                    "hidden rounded-full px-4 py-2 text-sm font-medium transition-colors lg:inline-flex",
                    transparent ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  onClick={() => section(l.id)}
                  data-testid={`nav-${l.id}`}
                  className={cn(
                    "hidden rounded-full px-4 py-2 text-sm font-medium transition-colors lg:inline-flex",
                    transparent ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {l.label}
                </button>
              )
            )}

          {user &&
            teamLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={cn(
                  "hidden rounded-full px-4 py-2 text-sm font-medium transition-colors lg:inline-flex",
                  pathname === l.to ? "bg-secondary text-foreground" : transparent ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {l.label}
              </Link>
            ))}

          {!user ? (
            <>
              <button
                onClick={goPlan}
                data-testid="nav-plan-btn"
                className="inline-flex items-center rounded-full bg-gold px-5 py-2 text-sm font-semibold text-primary transition-all hover:opacity-90 hover:shadow-lg"
              >
                Plan Your Event
              </button>
              <button
                onClick={login}
                data-testid="nav-signin-btn"
                className={cn(
                  "hidden rounded-full px-4 py-2 text-sm font-medium transition-colors sm:inline-flex",
                  transparent ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Sign in
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={goPlan} data-testid="nav-plan-btn" className="inline-flex items-center rounded-full bg-gold px-5 py-2 text-sm font-semibold text-primary transition-all hover:opacity-90">
                Plan Your Event
              </button>
              <div className="flex items-center gap-2 rounded-full border border-border py-1 pl-1 pr-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold text-xs font-bold text-primary">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="hidden text-sm font-medium sm:inline">{(user.name || user.email || "").split(" ")[0]}</span>
                <button onClick={handleLogout} data-testid="logout-btn" title="Sign out" className="text-muted-foreground hover:text-destructive">
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            className={cn(
              "inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors lg:hidden",
              transparent ? "text-white hover:bg-white/10" : "text-foreground hover:bg-secondary"
            )}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </div>

      {open && (
        <div className="lg:hidden" data-testid="mobile-menu">
          <div className="space-y-1 border-t border-border bg-background px-5 py-4 shadow-lg">
            {!user &&
              publicLinks.map((l) =>
                l.to ? (
                  <Link key={l.id} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}`} className="block rounded-xl px-4 py-3 text-sm font-medium text-foreground hover:bg-secondary">
                    {l.label}
                  </Link>
                ) : (
                  <button key={l.id} onClick={() => mobileSection(l.id)} data-testid={`mobile-nav-${l.id}`} className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-secondary">
                    {l.label}
                  </button>
                )
              )}
            {user &&
              teamLinks.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-${l.id}`} className={cn("block rounded-xl px-4 py-3 text-sm font-medium hover:bg-secondary", pathname === l.to ? "bg-secondary text-foreground" : "text-foreground")}>
                  {l.label}
                </Link>
              ))}
            <div className="mt-3 border-t border-border pt-3">
              {!user ? (
                <button onClick={() => { setOpen(false); login(); }} data-testid="mobile-signin-btn" className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-secondary">
                  Sign in
                </button>
              ) : (
                <button onClick={() => { setOpen(false); handleLogout(); }} data-testid="mobile-logout-btn" className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-destructive hover:bg-destructive/10">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
